import { useClickOutside } from "../../../shared/components/Dropdown/useClickOutside";
import type {
  AdminCrewReportVesselOption,
  AdminCrewReportVesselSelectorProps,
} from "../../models/admin-crew-report.models";
import {
  getCrewReportVesselSelectorPlaceholder,
  getFilteredAdminCrewReportVessels,
} from "../../utils/admin-crew-report.utils";
import AdminCrewReportVesselTable from "./AdminCrewReportVesselTable";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const AdminCrewReportVesselSelector = ({
  vessels,
  selectedVessel,
  loading,
  disabled = false,
  onSelectVessel,
}: AdminCrewReportVesselSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const closeVesselOptions = useCallback(() => {
    setIsOpen(false);
    setInputValue(selectedVessel?.vesselName ?? "");
  }, [selectedVessel]);
  const selectorDisabled = disabled || loading || vessels.length === 0;

  useClickOutside(closeVesselOptions, containerRef);

  useEffect(() => {
    if (selectorDisabled) {
      closeVesselOptions();
    }
  }, [closeVesselOptions, selectorDisabled]);

  useEffect(() => {
    if (selectedVessel) {
      setInputValue(selectedVessel.vesselName);
      return;
    }

    if (!isOpen) {
      setInputValue("");
    }
  }, [isOpen, selectedVessel]);

  const filteredVessels = useMemo(
    () => getFilteredAdminCrewReportVessels(vessels, inputValue, selectedVessel),
    [inputValue, selectedVessel, vessels],
  );

  const showClearButton = Boolean(inputValue || selectedVessel);
  const placeholderText = getCrewReportVesselSelectorPlaceholder(loading, vessels.length);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (selectorDisabled) {
      return;
    }

    if (selectedVessel) {
      onSelectVessel(undefined);
    }

    setInputValue(event.target.value);
    setIsOpen(true);
  };

  const handleSelectVessel = (vessel: AdminCrewReportVesselOption) => {
    if (selectorDisabled) {
      return;
    }

    setInputValue(vessel.vesselName);
    setIsOpen(false);
    onSelectVessel(vessel);
  };

  const clearSelection = () => {
    if (selectorDisabled) {
      return;
    }

    setInputValue("");
    setIsOpen(false);
    onSelectVessel(undefined);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative min-w-0" data-test="admin-crew-report-vessel-selector">
      <label className="sr-only" htmlFor="admin-crew-report-vessel">
        Vessel Details
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id="admin-crew-report-vessel"
          data-test="admin-crew-report-vessel-input"
          type="text"
          value={inputValue}
          placeholder={placeholderText}
          disabled={selectorDisabled}
          className="h-11 w-full min-w-0 border border-gray-300 bg-white px-3 pr-20 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-500 hover:border-green focus:border-green disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
          onFocus={() => !selectorDisabled && setIsOpen(true)}
          onClick={() => !selectorDisabled && setIsOpen(true)}
          onChange={handleInputChange}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              closeVesselOptions();
            }
          }}
        />
        {showClearButton && !selectorDisabled && (
          <button
            type="button"
            aria-label="Clear selected vessel"
            className="absolute right-10 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-[#4A5655] transition-colors hover:text-green dark:text-gray-200"
            onClick={clearSelection}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          aria-label="Toggle active vessel options"
          disabled={selectorDisabled}
          className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center text-[#4A5655] transition-colors hover:text-green disabled:cursor-not-allowed disabled:opacity-60 dark:text-gray-200"
          onClick={() => {
            if (selectorDisabled) {
              return;
            }

            setIsOpen((currentIsOpen) => !currentIsOpen);
            inputRef.current?.focus();
          }}
        >
          <ChevronDownIcon className={twMerge("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
        </button>
      </div>
      {isOpen && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden border border-[#D7E2DE] bg-white shadow-md dark:border-gray-700 dark:bg-gray-900">
          <AdminCrewReportVesselTable
            vessels={filteredVessels}
            selectedVessel={selectedVessel}
            onSelectVessel={handleSelectVessel}
          />
        </div>
      )}
    </div>
  );
};

export default AdminCrewReportVesselSelector;
